import { initializeApp } from 'firebase/app';
import { captureException } from '@sentry/react';
import { FlagsState, initialState } from 'data/slices/flags';
import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  Value,
} from 'firebase/remote-config';
import camelCase from 'camelcase';
import { useApplicationStore } from '@data';

const firebaseConfig = {
  apiKey: 'AIzaSyCLqghBR0ApepkRgsyD1aSoaGwiolOVNNs',
  authDomain: 'quickmd-c76a8.firebaseapp.com',
  projectId: 'quickmd-c76a8',
  storageBucket: 'quickmd-c76a8.appspot.com',
  messagingSenderId: '663932840781',
  appId: '1:663932840781:web:14740c21e51a005c8c2504',
  measurementId: 'G-C31541K379',
};

const firebase = initializeApp(firebaseConfig);

const remoteConfig = getRemoteConfig(firebase);

const isFastFetchEnabled = import.meta.env.DEV;
const DEV_REMOTE_CONFIG_FETCH_INTERVAL = 0;
const PROD_REMOTE_CONFIG_FETCH_INTERVAL = 3600000;

remoteConfig.settings.minimumFetchIntervalMillis = isFastFetchEnabled
  ? DEV_REMOTE_CONFIG_FETCH_INTERVAL
  : PROD_REMOTE_CONFIG_FETCH_INTERVAL;

/**
 * Converts a flag to its JSON representation.
 *
 * @param {import('@firebase/remote-config').Value} flag - The flag object to be converted.
 * @returns {Object} The JSON representation of the flag.
 */
const asJSON = (flag: Value) => JSON.parse(flag.asString());
const asBOOL = (flag: Value) => flag.asBoolean();
const asNumber = (flag: Value) => flag.asNumber();

export const FLAG_NAMES = {
  appointmentBufferMinutes: 'appointment_buffer_minutes',
  experimentCuratedAppointmentSlots: 'experiment_curated_appointment_slots',
  experimentCuratedAppointmentSlotsEnabled:
    'experiment_curated_appointment_slots_enabled',
  experimentMatQuestions: 'experiment_mat_questions',
  experimentMatQuestionsEnabled: 'experiment_mat_questions_enabled',
  idVerificationOptions: 'id_verification_options',
  legacyVerifiedOptions: 'legacy_verified_options',
  newBookingBanner: 'new_booking_banner',
  useProviderVisibility: 'use_provider_visibility',
  visitTranscriptConsent: 'visit_transcript_consent',
  adyen: 'adyen',
  mhtStates: 'mht_states',
  pharmacySearchDaw: 'pharmacy_search_daw',
} as const;

type FlagName = (typeof FLAG_NAMES)[keyof typeof FLAG_NAMES];

type FlagSpec = {
  default: FlagsState[keyof FlagsState];
  parse: (v: Value) => unknown;
};

const flagsConfig = {
  [FLAG_NAMES.legacyVerifiedOptions]: {
    default: initialState.legacyVerifiedOptions,
    parse: asJSON,
  },
  [FLAG_NAMES.idVerificationOptions]: {
    default: initialState.idVerificationOptions,
    parse: asJSON,
  },
  [FLAG_NAMES.newBookingBanner]: {
    default: initialState.newBookingBanner,
    parse: asBOOL,
  },
  [FLAG_NAMES.useProviderVisibility]: {
    default: initialState.useProviderVisibility,
    parse: asBOOL,
  },
  [FLAG_NAMES.appointmentBufferMinutes]: {
    default: initialState.appointmentBufferMinutes,
    parse: asNumber,
  },
  [FLAG_NAMES.experimentCuratedAppointmentSlots]: {
    default: false,
    parse: asBOOL,
  },
  [FLAG_NAMES.experimentCuratedAppointmentSlotsEnabled]: {
    default: false,
    parse: asBOOL,
  },
  [FLAG_NAMES.experimentMatQuestions]: {
    default: false,
    parse: asBOOL,
  },
  [FLAG_NAMES.experimentMatQuestionsEnabled]: {
    default: false,
    parse: asBOOL,
  },
  [FLAG_NAMES.visitTranscriptConsent]: {
    default: initialState.visitTranscriptConsent,
    parse: asBOOL,
  },
  [FLAG_NAMES.adyen]: {
    default: initialState.adyen,
    parse: asJSON,
  },
  [FLAG_NAMES.mhtStates]: {
    default: initialState.mhtStates,
    parse: asJSON,
  },
  [FLAG_NAMES.pharmacySearchDaw]: {
    default: initialState.pharmacySearchDaw,
    parse: asBOOL,
  },
} as const satisfies Record<FlagName, FlagSpec>;

const defaults = Object.keys(flagsConfig).reduce((flags, key) => {
  const value = flagsConfig[key]?.default;
  return { ...flags, [key]: value };
}, {});

function setConfigFlags() {
  const { flags } = useApplicationStore.getState();

  const currentFlags: FlagsState = Object.keys(flagsConfig).reduce(
    (acc, key) => {
      const name = `${import.meta.env.VITE_FEATURE_FLAGS_PREFIX}${key}`;

      const flagValue = getValue(remoteConfig, name);
      const parsedValue = flagsConfig[key]?.parse(flagValue);
      return { ...acc, [camelCase(key)]: parsedValue };
    },
    initialState,
  ) satisfies FlagsState;
  flags.setFlags(currentFlags);
  return currentFlags;
}

export async function getAndSetFeatureFlags() {
  remoteConfig.defaultConfig = defaults;

  try {
    await fetchAndActivate(remoteConfig);

    return setConfigFlags();
  } catch (e) {
    captureException(e);
    return useApplicationStore.getState().flags;
  }
}

// Utility function to ensure feature flags are fetched
export async function ensureFeatureFlagsFetched() {
  const flags = useApplicationStore.getState().flags;
  if (!flags.flagsHaveBeenFetched) {
    await getAndSetFeatureFlags();
  }
}
